import { Router } from '@angular/router';
import { NotificationSlugEnum } from '@app/generated/graphql';
import {
  STOCK_BASE_PATH,
  STOCK_PALLET_NUMBERS_QUERY_PARAM,
  STOCK_TAB_ACTIVE,
  STOCK_TAB_SHIPPED,
} from '@app/account/stock/config/constants';
import { INotificationConfig } from '../interfaces/interface';
import { DEFAULT_MESSAGE_LIFE } from './notification.constants';

const DISPLAY_TITLE: string = 'Pallet data has been updated';
const DISPLAY_DESCRIPTION: string =
  'Data on 1 pallet has changed. View updates on the stock screen.';

/**
 * Converts a camelCase, PascalCase, snake_case or kebab-case field name to a human readable string.
 * @param field - The field name to convert. This is used when there is only one field change.
 * @returns The human readable string.
 */
function humanizeFieldName(field: string): string {
  const spacedFieldName: string = field
    .replace(/([a-z\d])([A-Z])/g, '$1 $2') // handle camelCase and PascalCase
    .replace(/[_-]+/g, ' ') // handle snake_case and kebab-case
    .trim()
    .toLowerCase();

  if (!spacedFieldName) {
    return field;
  }

  return spacedFieldName.charAt(0).toUpperCase() + spacedFieldName.slice(1);
}

function resolveChangedFields(metadata: Record<string, unknown>): string[] {
  const changedFields: string[] = metadata['observed_changed_fields'] as
    | string[]
    | undefined;

  if (!changedFields) {
    return [];
  }

  return changedFields;
}

function resolveFieldLabel(
  metadata: Record<string, unknown>,
): string | undefined {
  const [firstField]: string[] = resolveChangedFields(metadata);

  if (!firstField) {
    return undefined;
  }

  return humanizeFieldName(firstField);
}

function resolvePalletCount(metadata: Record<string, unknown>): number {
  return Number(metadata['item_count']) || 0;
}

function isSingleFieldChange(metadata: Record<string, unknown>): boolean {
  const isSingleFieldChange: boolean = metadata['single_field_change'] as
    | boolean
    | undefined;

  if (!isSingleFieldChange) {
    return false;
  }

  return true;
}

function resolveStockTabPath(metadata: Record<string, unknown>): string {
  const stockType: string = metadata['stock_type'] as string;
  const stockTypes: string[] = metadata['stock_types'] as string[];

  const candidates: string[] = [stockType, ...(stockTypes || [])];

  const isShipped: boolean = candidates.some(
    (value: string) =>
      typeof value === 'string' && value.toLowerCase() === 'shipped',
  );

  return isShipped ? STOCK_TAB_SHIPPED.path : STOCK_TAB_ACTIVE.path;
}

function resolvePalletNumbers(metadata: Record<string, unknown>): string[] {
  const palletNumbers: unknown = metadata['pallet_numbers'];

  if (!Array.isArray(palletNumbers)) {
    return [];
  }

  return palletNumbers
    .map((palletNumber: unknown) => String(palletNumber ?? '').trim())
    .filter((palletNumber: string) => palletNumber.length > 0);
}

export function createPalletDataChangeNotificationConfig(
  router: Router,
): INotificationConfig {
  return {
    slug: NotificationSlugEnum.PalletDataChange,
    preference: {
      displayTitle: 'Pallet data changes',
      displayDescription:
        'Get notified when pallet data such as weight, target market, buyer etc. has changed.',
      defaultConfig: {},
    },
    message: {
      iconPath: '/assets/icons/general/info.svg',
      displayTitle: DISPLAY_TITLE,
      displayDescription: DISPLAY_DESCRIPTION,
      resolveDisplayTitle: (): string => DISPLAY_TITLE,
      resolveDisplayDescription: (
        metadata: Record<string, unknown>,
      ): string | undefined => {
        const count: number = resolvePalletCount(metadata);
        const unit: string = count === 1 ? 'pallet' : 'pallets';

        if (isSingleFieldChange(metadata)) {
          const fieldLabel: string | undefined =
            resolveFieldLabel(metadata) ?? 'Data';
          return `${fieldLabel} has been changed on ${count} ${unit}. View updates on the stock screen.`;
        }

        return `Data on ${count} ${unit} has changed. View updates on the stock screen.`;
      },
      actionLabel: 'View updated pallets',
      action: (metadata: Record<string, unknown>) => {
        const tabPath: string = resolveStockTabPath(metadata);
        const palletNumbers: string[] = resolvePalletNumbers(metadata);

        router.navigate([STOCK_BASE_PATH, tabPath], {
          queryParams: palletNumbers.length
            ? {
                [STOCK_PALLET_NUMBERS_QUERY_PARAM]: palletNumbers.join(','),
              }
            : {},
        });
      },
      life: DEFAULT_MESSAGE_LIFE,
    },
  };
}
